import type { ReactNode } from 'react'

import type { GrowerReportPeriod, UsageChart } from '@/lib/my-grower-report'
import { PeriodBarChart } from '@/components/charts/period-bar-chart'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { agriUnitLabel } from '@/lib/agri-unit-label'
import { formatQty, formatVnd } from '@/lib/format'
import {
    buildMoneyPeriodLines,
    buildUsagePeriodLines,
    formatCompactAmount,
    periodAxisLabel,
    periodHumanLabel,
    periodRangeLabel,
} from '@/lib/my-grower-report'
import { m } from '@/lib/paraglide/messages'

// Người xem là hộ trồng chè, đa số không quen đọc biểu đồ (yêu cầu bổ sung
// M-45). Mỗi khối vì vậy đọc được KHÔNG cần biểu đồ: số tổng chữ to → danh sách
// từng kỳ bằng lời ("Tuần này", "Tuần trước"…) → biểu đồ chỉ để xem thêm.

interface QueryState {
    isLoading: boolean
    isError: boolean
    onRetry: () => void
}

const BY_PERIOD_HEADING: Record<GrowerReportPeriod, () => string> = {
    week: () => m.my_grower_report_by_week(),
    month: () => m.my_grower_report_by_month(),
    year: () => m.my_grower_report_by_year(),
}

/** Khung chung của một khối: tiêu đề + trạng thái tải / lỗi / trống. */
function ReportCard({
    title,
    state,
    isEmpty,
    emptyText,
    note,
    children,
}: {
    title: string
    state: QueryState
    isEmpty: boolean
    emptyText: string
    note?: ReactNode
    children: ReactNode
}) {
    let body: ReactNode = children
    if (state.isLoading) {
        body = (
            <p className="py-6 text-center text-lg text-muted-foreground">
                {m.my_grower_report_loading()}
            </p>
        )
    } else if (state.isError) {
        body = (
            <div className="space-y-3 py-4 text-center">
                <p className="text-lg text-muted-foreground">
                    {m.my_grower_report_load_error()}
                </p>
                <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="text-lg"
                    onClick={state.onRetry}
                >
                    {m.my_grower_report_retry()}
                </Button>
            </div>
        )
    } else if (isEmpty) {
        body = (
            <p className="py-6 text-center text-lg text-muted-foreground">
                {emptyText}
            </p>
        )
    }
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-2xl">{title}</CardTitle>
                {note}
            </CardHeader>
            <CardContent className="space-y-6">{body}</CardContent>
        </Card>
    )
}

function Note({ children }: { children: ReactNode }) {
    return <p className="text-base text-muted-foreground">{children}</p>
}

function SubHeading({ children }: { children: ReactNode }) {
    return <h3 className="text-lg font-semibold">{children}</h3>
}

function qtyWithUnit(n: number, unit: string) {
    const label = agriUnitLabel(unit)
    return label ? `${formatQty(n)} ${label}` : formatQty(n)
}

/** Lượng phân bón / thuốc: tổng từng vật tư → từng kỳ → biểu đồ mỗi đơn vị. */
export function UsageSection({
    title,
    charts,
    period,
    starts,
    rangeText,
    hasSplit,
    state,
}: {
    title: string
    charts: UsageChart[]
    period: GrowerReportPeriod
    /** Các kỳ đang xem, cũ → mới. */
    starts: string[]
    rangeText: string
    hasSplit: boolean
    state: QueryState
}) {
    const lines = buildUsagePeriodLines(charts, starts)
    return (
        <ReportCard
            title={title}
            state={state}
            isEmpty={charts.length === 0}
            emptyText={m.my_grower_report_empty_usage()}
            note={
                hasSplit ? (
                    <Note>{m.my_grower_report_split_note()}</Note>
                ) : undefined
            }
        >
            <section className="space-y-2">
                <SubHeading>{m.my_grower_report_total_heading()}</SubHeading>
                <table className="w-full text-lg">
                    <thead className="sr-only">
                        <tr>
                            <th>{m.my_grower_report_col_material()}</th>
                            <th>{m.my_grower_report_col_total()}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {charts.flatMap((chart) =>
                            chart.materials.map((mat) => (
                                <tr
                                    key={`${chart.unit}-${mat.materialId}`}
                                    className="border-b last:border-0"
                                >
                                    <td className="py-2.5 pr-3">{mat.name}</td>
                                    <td className="py-2.5 text-right text-xl font-semibold tabular-nums whitespace-nowrap">
                                        {qtyWithUnit(mat.total, chart.unit)}
                                    </td>
                                </tr>
                            )),
                        )}
                    </tbody>
                </table>
            </section>

            <section className="space-y-2">
                <SubHeading>{BY_PERIOD_HEADING[period]()}</SubHeading>
                <ul className="space-y-2">
                    {lines.map((line) => (
                        <li
                            key={line.period}
                            className="rounded-xl bg-muted/50 px-3 py-2.5"
                        >
                            <p className="text-base font-medium text-muted-foreground">
                                {periodHumanLabel(period, line.period, starts)}
                            </p>
                            <ul className="mt-1 space-y-0.5 text-lg">
                                {line.items.map((item) => (
                                    <li
                                        key={`${item.unit}-${item.materialId}`}
                                        className="flex justify-between gap-3"
                                    >
                                        <span>{item.name}</span>
                                        <span className="font-semibold tabular-nums whitespace-nowrap">
                                            {qtyWithUnit(item.qty, item.unit)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="space-y-3">
                <SubHeading>{m.my_grower_report_chart_heading()}</SubHeading>
                <Note>{m.my_grower_report_chart_hint()}</Note>
                {charts.map((chart) => {
                    const unit = agriUnitLabel(chart.unit) || chart.unit
                    return (
                        <div key={chart.unit} className="space-y-2">
                            <p className="text-base font-medium">
                                {m.my_grower_report_unit_heading({ unit })}
                            </p>
                            <PeriodBarChart
                                ariaLabel={m.my_grower_report_chart_label({
                                    title: `${title} (${unit})`,
                                    range: rangeText,
                                })}
                                rows={chart.rows}
                                series={chart.series}
                                axisLabel={(p) => periodAxisLabel(period, p)}
                                rangeLabel={(p) => periodRangeLabel(period, p)}
                                formatValue={(v) => qtyWithUnit(v, chart.unit)}
                                formatAxisValue={formatQty}
                                showValueLabels
                            />
                        </div>
                    )
                })}
            </section>
        </ReportCard>
    )
}

/** Tiền chè đã nhận: tổng cả khoảng (khớp trang "Tiền chè đã nhận") → từng kỳ → biểu đồ. */
export function MoneySection({
    rows,
    total,
    count,
    period,
    starts,
    rangeText,
    areaFiltered,
    state,
}: {
    rows: Array<{ period: string; amount: number }>
    total: number
    count: number
    period: GrowerReportPeriod
    /** Các kỳ đang xem, cũ → mới. */
    starts: string[]
    rangeText: string
    areaFiltered: boolean
    state: QueryState
}) {
    const title = m.my_grower_report_money_title()
    const series = [{ key: 'amount', name: title, color: 'var(--chart-1)' }]
    const lines = buildMoneyPeriodLines(rows)
    return (
        <ReportCard
            title={title}
            state={state}
            isEmpty={count === 0}
            emptyText={m.my_grower_report_empty_money()}
            note={
                areaFiltered ? (
                    <Note>{m.my_grower_report_money_area_note()}</Note>
                ) : undefined
            }
        >
            {/* Nhãn + một con số to: khớp dòng "Tổng" của trang "Tiền chè đã
                nhận" cùng khoảng ngày. */}
            <section
                className="space-y-1"
                aria-label={m.my_grower_report_money_total_label()}
            >
                <p className="text-base text-muted-foreground">
                    {m.my_grower_report_money_total_label()}
                </p>
                <p className="text-3xl font-bold tabular-nums">
                    {formatVnd(total)}
                </p>
            </section>

            <section className="space-y-2">
                <SubHeading>{BY_PERIOD_HEADING[period]()}</SubHeading>
                <ul className="space-y-2">
                    {lines.map((line) => (
                        <li
                            key={line.period}
                            className="rounded-xl bg-muted/50 px-3 py-2.5"
                        >
                            {/* Nhãn kỳ trên, số tiền dưới: chung một hàng
                                thì "Tuần trước (05/10 – 11/10)" rớt dòng. */}
                            <p className="text-base font-medium text-muted-foreground">
                                {periodHumanLabel(period, line.period, starts)}
                            </p>
                            <p className="mt-1 text-xl font-semibold tabular-nums">
                                {formatVnd(line.amount)}
                            </p>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="space-y-3">
                <SubHeading>{m.my_grower_report_chart_heading()}</SubHeading>
                <Note>{m.my_grower_report_money_chart_hint()}</Note>
                <PeriodBarChart
                    ariaLabel={m.my_grower_report_chart_label({
                        title,
                        range: rangeText,
                    })}
                    rows={rows}
                    series={series}
                    axisLabel={(p) => periodAxisLabel(period, p)}
                    rangeLabel={(p) => periodRangeLabel(period, p)}
                    formatValue={formatVnd}
                    formatAxisValue={formatCompactAmount}
                    showValueLabels
                />
            </section>
        </ReportCard>
    )
}
